// Provides a single entry point for updating a tall cabinet's 3D representation
// when its section layout changes from the UI. Safe to call when 3D is not open.

import SqftStore from '../../../../../../canvas/store/sqftStore';
import workspace from '../../../../../../canvas/core/workspace';
import {
  applyTallCabinetLayout,
  applyTallCabinetSectionHighlights,
} from './tallCabinetInteriorLayoutApplier';
import {
  applyTallCabinetGeometry,
  applyTallCabinetHeight,
  applyTallCabinetMaterialUvProjection,
  applyTallCabinetWidthDepth,
} from '../tallCabinetGeometry/tallCabinetGeometryResizer';
import { getCabinetInteriorHeight} from '../../../../../../../helpers/tallCabinetInteriorHelper';
import { applyDoorStateForInstance, resetDoorsForInstance } from './tallCabinetDoorsAnimator';

/**
 * Find the live Three.js cabinet instance for a given shape ID.
 * Returns null when the 3D scene is not active or the shape is not loaded.
 *
 * @param {string} shapeId
 * @returns {THREE.Object3D|null}
 */
function findCabinetInstance(shapeId) {
  if (!SqftStore.viewGL) {
    return null;
  }

  const loader = SqftStore.viewGL.modelsLoader?.loaders?.cabinets?.loader;
  if (!loader) {
    return null;
  }

  for (const inst of loader.instances.values()) {
    if (inst.userData.shapeId === shapeId) {
      return { inst, loader };
    }
  }

  return null;
}

function updateCabinetPosition(inst, loader, shape) {
  const originPointOnWorkspace = shape.getOriginPointOnWorkspace();
  const modelScale = loader.modelScale || 1;

  inst.position.x = originPointOnWorkspace.x * modelScale;
  inst.position.z = originPointOnWorkspace.y * modelScale;
}

/**
 * Update only the 3D position of a cabinet instance to match its current 2D canvas position.
 * Used to sync adjacent cabinets after auto-shift from a neighbour resize.
 *
 * @param {string} shapeId
 */
export function updateCabinetPositionIn3D(shapeId) {
  const cabinetInstance = findCabinetInstance(shapeId);
  if (!cabinetInstance) {
    return;
  }
  const { inst, loader } = cabinetInstance;
  const shape = workspace.getShapeById(shapeId);
  if (!shape) {
    return;
  }
  updateCabinetPosition(inst, loader, shape);

  const crownMoldingLoader = SqftStore.viewGL?.modelsLoader?.loaders?.cabinets?.loader?.crownMoldingLoader;
  if (crownMoldingLoader) {
    crownMoldingLoader.updatePositionForShape(shapeId);
  }
}

/**
 * Apply a new section layout to the tall cabinet's live 3D instance.
 * No-op if the 3D scene is not currently open.
 *
 * @param {string} shapeId
 * @param {{alignment: string, sections: Array<{type: string, height: number, quantity?: number}>}} layout
 */
export function updateTallCabinetIn3D(shapeId, layout) {
  const cabinetInstance = findCabinetInstance(shapeId);
  if (!cabinetInstance) {
    return;
  }

  const { inst, loader } = cabinetInstance;
  const shape = workspace.getShapeById(shapeId);
  const cabinetHeight = shape.getHeight();
  const cabinetInteriorHeight = getCabinetInteriorHeight(cabinetHeight);

  updateCabinetPosition(inst, loader, shape);
  inst.userData.shapeHeight = cabinetHeight;
  inst.userData.cabinetInteriorHeight = cabinetInteriorHeight;

  if (!layout) {
    applyTallCabinetGeometry(inst, shape);
    return;
  }

  applyTallCabinetHeight(inst, shape);
  resetDoorsForInstance(inst);
  applyTallCabinetLayout(inst, {
    ...layout,
    doorExtensionEnabled: shape.shapeCabinet.getDoorExtensionEnabled(),
  });
  applyDoorStateForInstance(inst);
  applyTallCabinetWidthDepth(inst, shape);
  applyTallCabinetMaterialUvProjection(inst, shape);
  applyTallCabinetSectionHighlights(inst, layout);
}
